import React, { ReactNode, useEffect } from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { createTheme, loadTheme } from "office-ui-fabric-react";
import { SkeletonTheme } from "react-loading-skeleton";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

export const lightTheme = createTheme({
  palette: {
    themePrimary: "#005aa1",
    themeLighterAlt: "#f1f7fb",
    themeLighter: "#cadff0",
    themeLight: "#9fc5e3",
    themeTertiary: "#4f93c6",
    themeSecondary: "#156aac",
    themeDarkAlt: "#005291",
    themeDark: "#00457a",
    themeDarker: "#00335a",
    neutralLighterAlt: "#faf9f8",
    neutralLighter: "#f3f2f1",
    neutralLight: "#edebe9",
    neutralQuaternaryAlt: "#e1dfdd",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c6c4",
    neutralTertiary: "#a19f9d",
    neutralSecondary: "#605e5c",
    neutralPrimaryAlt: "#3b3a39",
    neutralPrimary: "#323130",
    neutralDark: "#201f1e",
    black: "#000000",
    white: "#ffffff"
  }
});

export const darkTheme = createTheme({
  palette: {
    themePrimary: "#38a9ff",
    themeLighterAlt: "#02070a",
    themeLighter: "#091b29",
    themeLight: "#11334d",
    themeTertiary: "#226599",
    themeSecondary: "#3195e0",
    themeDarkAlt: "#4cb1ff",
    themeDark: "#68bdff",
    themeDarker: "#90cfff",
    neutralLighterAlt: "#212121",
    neutralLighter: "#2a2a2a",
    neutralLight: "#393939",
    neutralQuaternaryAlt: "#424242",
    neutralQuaternary: "#494949",
    neutralTertiaryAlt: "#686868",
    neutralTertiary: "#c8c8c8",
    neutralSecondary: "#d0d0d0",
    neutralPrimaryAlt: "#dadada",
    neutralPrimary: "#ffffff",
    neutralDark: "#f4f4f4",
    black: "#f8f8f8",
    white: "#171717"
  }
});

type Props = {
  isDarkMode: boolean;
  children: ReactNode;
};

export const ThemeProvider = ({ isDarkMode, children }: Props) => {
  useEffect(() => {
    if (isDarkMode) {
      loadTheme(darkTheme);
    } else {
      loadTheme(lightTheme);
    }
  }, [isDarkMode]);

  return (
    <EmotionThemeProvider
      theme={isDarkMode ? { ...darkTheme, isDark: true } : lightTheme}
    >
      <StyledThemeProvider
        theme={isDarkMode ? { ...darkTheme, isDark: true } : lightTheme}
      >
        <SkeletonTheme
          color={isDarkMode ? darkTheme.palette.neutralLighter : undefined}
          highlightColor={
            isDarkMode ? darkTheme.palette.neutralQuaternaryAlt : undefined
          }
        >
          {children}
        </SkeletonTheme>
      </StyledThemeProvider>
    </EmotionThemeProvider>
  );
};
